import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from './documentRegistry';

export interface DocumentAnalysis {
  summary: string;
  expiryDate?: Date | null;
  extractedDates: { type: string; date: Date; confidence: string }[];
  documentType?: string;
  keyInformation: Record<string, string>;
}

export interface SignatureMetadata {
  signatureUrl?: string; // Firebase Storage URL if uploaded
  signatureDataUrl?: string; // Base64 data URL (alternative storage)
  signerName?: string;
  signerEmail?: string;
  signedAt: Date | string | number;
  signatureType: 'draw' | 'typed' | 'upload';
}

export interface AssetEvidenceItem {
  key: string;
  label: string;
  type: string;
  url: string;
  fileName?: string;
}

export interface AssetEvidenceEntry {
  assetId: string;
  assetName: string;
  evidence: AssetEvidenceItem[];
}

export interface TaskCompletion {
  id?: string;
  taskId: string;
  templateName: string;
  locationName: string;
  locationId: string;
  evidenceUrls: string[]; // URLs of uploaded evidence files
  evidenceFileNames?: string[]; // Original filenames for mapping analyses
  documentAnalyses?: Record<string, DocumentAnalysis>; // File name -> analysis results
  fileNotes?: Record<string, string>; // File name -> user notes for each file
  assetEvidence?: AssetEvidenceEntry[]; // Evidence captured per asset
  comments: string;
  notes: string;
  completionDate: Date;
  completedBy?: string; // User ID
  completedByName?: string; // Formatted display name (e.g., "Name (email)")
  reviewedBy?: string;
  reviewedAt?: any;
  rejected?: boolean; // Whether this completion was rejected
  rejectedBy?: string; // Email of reviewer who rejected it
  rejectedAt?: any; // Timestamp when rejected
  rejectionReason?: string; // Reason for rejection
  checklistId?: string; // ID of attached checklist
  checklistStatus?: Record<string, boolean>; // Checklist item status
  signature?: SignatureMetadata; // Electronic signature data
  owner_role?: string; // Owner role from compliance template for access control
  organizationId?: string; // Organization ID (may not be present in older documents)
  createdAt?: any;
  updatedAt?: any;
  manualNextDueDate?: Date | number | string | null;
  aiSuggestedDueDate?: Date | number | string | null;
  dueDateOverrideSource?: 'manual' | 'ai' | 'frequency';
}

export interface EvidenceUploadContext extends Partial<DocumentUploadContext> {
  evidenceLabel?: string;
  storagePath?: string;
}

export interface StorageUploadResult {
  url: string;
  storagePath: string;
}

// Import shared filename sanitization utilities
import { sanitizeFileName, sanitizeStoragePath } from '@/utils/fileNameSanitizer';

// Upload file to Firebase Storage and get URL / storage path
export async function uploadEvidenceFile(
  file: File,
  context: EvidenceUploadContext = {}
): Promise<StorageUploadResult> {
  console.log('🔵 uploadEvidenceFile called with:', file.name, file.size, 'bytes');

  // Validate file size before attempting upload
  const { validateFileSize } = await import('@/utils/fileSizeValidation');
  validateFileSize(file);

  if (!storage) {
    console.error('❌ Firebase Storage not initialized');
    throw new Error('Firebase Storage not initialized. Please check your Firebase configuration.');
  }

  try {
    const docType = context.docType || 'evidence';
    const uploadedAt = new Date().toISOString();

    // Determine storage path
    const timestamp = Date.now();
    const defaultFileName = `${timestamp}_${sanitizeFileName(file.name) || 'file'}`;
    const providedPath = context.storagePath ? sanitizeStoragePath(context.storagePath) : undefined;
    const storagePath = providedPath || `evidence/${defaultFileName}`;
    console.log('📝 Creating storage reference:', storagePath);
    
    // Create a storage reference
    const storageRef = ref(storage, storagePath);
    
    const storageContext: DocumentUploadContext = {
      docType,
      organizationId: context.organizationId,
      locationId: context.locationId,
      taskId: context.taskId,
      checklistId: context.checklistId,
      formId: context.formId,
      agreementId: context.agreementId,
      assetId: context.assetId,
      uploadedBy: context.uploadedBy,
      templateId: context.templateId,
      title: context.title || file.name,
      source: context.source || 'web',
      additionalMetadata: {
        evidenceLabel: context.evidenceLabel,
        ...context.additionalMetadata,
      },
    };

    // Upload the file
    console.log('⬆️ Uploading file to Firebase Storage...');
    await uploadBytes(
      storageRef,
      file,
      createStorageMetadata(storageContext, {
        contentType: file.type,
        uploadedAt,
      })
    );
    console.log('✅ File bytes uploaded successfully');
    
    // Get the download URL
    console.log('🔗 Getting download URL...');
    const downloadURL = await getDownloadURL(storageRef);
    console.log('✅ Download URL:', downloadURL);
    
    await recordDocumentMetadata({
      storagePath: storageRef.fullPath,
      bucket: (storageRef as any).bucket,
      downloadUrl: downloadURL,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadedAt,
      context: storageContext,
    });

    return { url: downloadURL, storagePath: storageRef.fullPath };
  } catch (error: any) {
    console.error('❌ Error uploading file:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    // Provide helpful error messages
    if (error.code === 'storage/unauthorized' || error.code === 'storage/permission-denied') {
      // Check if error might be due to file size (Firebase rules reject large files with permission error)
      const { MAX_FILE_SIZE, formatFileSize } = await import('@/utils/fileSizeValidation');
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeFormatted = formatFileSize(file.size);
        const maxSizeFormatted = formatFileSize(MAX_FILE_SIZE);
        throw new Error(
          `File "${file.name}" is too large (${fileSizeFormatted}). Maximum file size is ${maxSizeFormatted}. Please compress the file or choose a smaller file.`
        );
      }
      throw new Error('Permission denied. Please check Firebase Storage rules.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Storage quota exceeded. Please contact administrator.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('Unknown storage error. Please try again.');
    } else {
      throw new Error(`File upload failed: ${error.message}`);
    }
  }
}

// Save task completion to Firestore
export const taskCompletionService = {
  // Add a task completion record
  addCompletion: async (completionData: TaskCompletion) => {
    try {
      const completionsRef = collection(db, 'taskCompletions');
      
      // Convert completionDate to Firestore Timestamp if it's a Date object
      const completionDate = completionData.completionDate instanceof Date 
        ? completionData.completionDate.getTime() 
        : completionData.completionDate;
      
      const manualNextDueDate = completionData.manualNextDueDate instanceof Date
        ? completionData.manualNextDueDate.getTime()
        : completionData.manualNextDueDate ?? null;

      const aiSuggestedDueDate = completionData.aiSuggestedDueDate instanceof Date
        ? completionData.aiSuggestedDueDate.getTime()
        : completionData.aiSuggestedDueDate ?? null;

      const dataToSave: any = {
        ...completionData,
        // Trim whitespace from locationName if provided
        ...(completionData.locationName && { locationName: completionData.locationName.trim() }),
        completionDate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (completionData.manualNextDueDate !== undefined) {
        dataToSave.manualNextDueDate = manualNextDueDate;
      }

      if (completionData.aiSuggestedDueDate !== undefined) {
        dataToSave.aiSuggestedDueDate = aiSuggestedDueDate;
      }

      Object.keys(dataToSave).forEach((key) => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key];
        }
      });

      const docRef = await addDoc(completionsRef, dataToSave);
      
      console.log('✅ Task completion saved to Firestore with ID:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding task completion:', error);
      return { success: false, error: error.message };
    }
  },

  updateCompletion: async (completionId: string, updates: Partial<TaskCompletion>) => {
    try {
      const completionRef = doc(db, 'taskCompletions', completionId);

      const updateData: any = { ...updates, updatedAt: serverTimestamp() };

      if (updates.manualNextDueDate !== undefined) {
        updateData.manualNextDueDate = updates.manualNextDueDate === null
          ? null
          : updates.manualNextDueDate instanceof Date
            ? updates.manualNextDueDate.getTime()
            : updates.manualNextDueDate;
      }

      if (updates.aiSuggestedDueDate !== undefined) {
        updateData.aiSuggestedDueDate = updates.aiSuggestedDueDate === null
          ? null
          : updates.aiSuggestedDueDate instanceof Date
            ? updates.aiSuggestedDueDate.getTime()
            : updates.aiSuggestedDueDate;
      }

      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(completionRef, updateData);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating task completion:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all task completions for a specific task
  getCompletionsForTask: async (taskId: string) => {
    try {
      const completionsRef = collection(db, 'taskCompletions');
      // Remove orderBy to avoid requiring an index
      const q = query(
        completionsRef,
        where('taskId', '==', taskId)
      );
      const querySnapshot = await getDocs(q);
      
      const completions: TaskCompletion[] = [];
      querySnapshot.forEach((doc) => {
        completions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
      });
      
      // Sort by completionDate in memory (descending)
      completions.sort((a, b) => {
        const dateA = a.completionDate instanceof Date ? a.completionDate : new Date(a.completionDate);
        const dateB = b.completionDate instanceof Date ? b.completionDate : new Date(b.completionDate);
        return dateB.getTime() - dateA.getTime();
      });
      
      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting task completions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all task completions
  // Note: taskCompletions collection doesn't have organizationId, so we need to filter by locationId
  // The component should provide locationIds to filter by, or we fetch all and filter in memory
  getAllCompletions: async (organizationId?: string, locationIds?: string[]) => {
    try {
      const completionsRef = collection(db, 'taskCompletions');
      let q;
      
      // Task completions don't have organizationId stored directly
      // Instead, filter by locationIds if provided (locations have organizationId)
      let completions: TaskCompletion[] = [];
      
      if (locationIds && locationIds.length > 0) {
        // Firestore 'in' query supports up to 10 items
        if (locationIds.length <= 10) {
          try {
            q = query(
              completionsRef,
              where('locationId', 'in', locationIds),
              orderBy('completionDate', 'desc')
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
              completions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
            });
          } catch (error: any) {
            // If index is missing, fall back to fetching all and filtering in memory
            if (error.code === 'failed-precondition') {
              console.warn(`[taskCompletionService] Index missing for locationId + completionDate query. Fetching all and filtering in memory.`);
              q = query(completionsRef, orderBy('completionDate', 'desc'));
              const querySnapshot = await getDocs(q);
              querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (locationIds.includes(data.locationId)) {
                  completions.push({ id: doc.id, ...data } as TaskCompletion);
                }
              });
            } else {
              throw error;
            }
          }
        } else {
          // If more than 10 locations, batch queries in chunks of 10
          console.log(`[taskCompletionService] Batching ${locationIds.length} locationIds into chunks of 10`);
          const chunks: string[][] = [];
          for (let i = 0; i < locationIds.length; i += 10) {
            chunks.push(locationIds.slice(i, i + 10));
          }
          
          // Execute queries for each chunk in parallel
          const chunkQueries = chunks.map(async (chunk) => {
            try {
              const chunkQ = query(
                completionsRef,
                where('locationId', 'in', chunk),
                orderBy('completionDate', 'desc')
              );
              const chunkSnapshot = await getDocs(chunkQ);
              const chunkCompletions: TaskCompletion[] = [];
              chunkSnapshot.forEach((doc) => {
                chunkCompletions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
              });
              return chunkCompletions;
            } catch (error: any) {
              // If index missing, try without orderBy for this chunk
              if (error.code === 'failed-precondition') {
                console.warn(`[taskCompletionService] Index missing for chunk, fetching without orderBy`);
                const chunkQ = query(completionsRef, where('locationId', 'in', chunk));
                const chunkSnapshot = await getDocs(chunkQ);
                const chunkCompletions: TaskCompletion[] = [];
                chunkSnapshot.forEach((doc) => {
                  chunkCompletions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
                });
                return chunkCompletions;
              }
              throw error;
            }
          });
          
          const chunkResults = await Promise.all(chunkQueries);
          completions = chunkResults.flat();
          
          // Sort all results by completionDate (descending)
          completions.sort((a, b) => {
            const dateA = a.completionDate instanceof Date ? a.completionDate.getTime() : 
                         (typeof a.completionDate === 'number' ? a.completionDate : 0);
            const dateB = b.completionDate instanceof Date ? b.completionDate.getTime() : 
                         (typeof b.completionDate === 'number' ? b.completionDate : 0);
            return dateB - dateA;
          });
        }
      } else {
        // No locationIds provided - fetch all (will be filtered by locationId in component)
        try {
          q = query(completionsRef, orderBy('completionDate', 'desc'));
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            completions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
          });
        } catch (error: any) {
          // If index missing, try without orderBy
          if (error.code === 'failed-precondition') {
            console.warn(`[taskCompletionService] Index missing for completionDate. Fetching without orderBy.`);
            q = query(completionsRef);
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
              completions.push({ id: doc.id, ...doc.data() } as TaskCompletion);
            });
            // Sort in memory
            completions.sort((a, b) => {
              const dateA = a.completionDate instanceof Date ? a.completionDate.getTime() : 
                           (typeof a.completionDate === 'number' ? a.completionDate : 0);
              const dateB = b.completionDate instanceof Date ? b.completionDate.getTime() : 
                           (typeof b.completionDate === 'number' ? b.completionDate : 0);
              return dateB - dateA;
            });
          } else {
            throw error;
          }
        }
      }
      
      // Return results (already filtered by locationIds if provided)
      console.log(`[taskCompletionService] Retrieved ${completions.length} task completions${locationIds ? ` filtered by ${locationIds.length} locationIds` : ' (no location filter)'}`);
      return { success: true, data: completions };
    } catch (error: any) {
      console.error('Error getting all task completions:', error);
      // If the query fails (e.g., missing index), try without orderBy and sort in memory
      if (error.code === 'failed-precondition') {
        console.warn('Query failed due to missing index, trying without orderBy:', error);
        try {
          const completionsRef = collection(db, 'taskCompletions');
          let q;
          
          // Try with organizationId filter but without orderBy
          if (organizationId) {
            q = query(completionsRef, where('organizationId', '==', organizationId));
          } else {
            // If no organizationId, just get all without orderBy
            q = query(completionsRef);
          }
          
          const querySnapshot = await getDocs(q);
          const completions: TaskCompletion[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Filter in memory by organizationId if it exists on the document
            // If document doesn't have organizationId, include it (will be filtered by locationId in component)
            // Note: If the query already filtered by organizationId, this is redundant but safe
            if (!organizationId || !data.organizationId || data.organizationId === organizationId) {
              completions.push({ id: doc.id, ...data } as TaskCompletion);
            }
          });
          
          // Sort in memory by completionDate (descending)
          completions.sort((a, b) => {
            const dateA = a.completionDate instanceof Date ? a.completionDate.getTime() : 
                         (typeof a.completionDate === 'number' ? a.completionDate : 0);
            const dateB = b.completionDate instanceof Date ? b.completionDate.getTime() : 
                         (typeof b.completionDate === 'number' ? b.completionDate : 0);
            return dateB - dateA; // Descending order
          });
          
          console.log(`[taskCompletionService] Fallback query succeeded: ${completions.length} completions (sorted in memory)`);
          return { success: true, data: completions };
        } catch (fallbackError: any) {
          console.error('Fallback query also failed:', fallbackError);
          // Last resort: get all documents without any filters
          // Note: Some documents may not have organizationId set, so we return all
          // and let the component filter by locationId instead
          try {
            const completionsRef = collection(db, 'taskCompletions');
            const querySnapshot = await getDocs(completionsRef);
            const completions: TaskCompletion[] = [];
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              // Filter in memory by organizationId if it exists on the document
              // If document doesn't have organizationId, include it (will be filtered by locationId in component)
              if (!organizationId || !data.organizationId || data.organizationId === organizationId) {
                completions.push({ id: doc.id, ...data } as TaskCompletion);
              }
            });
            
            // Sort in memory
            completions.sort((a, b) => {
              const dateA = a.completionDate instanceof Date ? a.completionDate.getTime() : 
                           (typeof a.completionDate === 'number' ? a.completionDate : 0);
              const dateB = b.completionDate instanceof Date ? b.completionDate.getTime() : 
                           (typeof b.completionDate === 'number' ? b.completionDate : 0);
              return dateB - dateA;
            });
            
            console.log(`[taskCompletionService] Last resort query succeeded: ${completions.length} completions (${organizationId ? 'some may not have organizationId' : 'no org filter'})`);
            return { success: true, data: completions };
          } catch (lastResortError: any) {
            console.error('Last resort query failed:', lastResortError);
            return { success: false, error: lastResortError.message };
          }
        }
      }
      return { success: false, error: error.message };
    }
  },
};

